import ms from 'milsymbol';

import { sidcFor, type SidcAffiliation, type SidcSegment } from '../model/sidc.js';
import type { Rung } from '@badger-sentinel/protocol';

/**
 * A MIL-STD-2525E / APP-6(E) symbol, generated by milsymbol from a SIDC (Q4).
 *
 * The code comes from `model/sidc.ts`, which is a constant table keyed by
 * `typeId` and built exactly the way the two vendored map renderers build
 * theirs. Phases 5 and 6 lift this component for markers, which is the second
 * and third use the ledger expects.
 *
 * ## Why `dangerouslySetInnerHTML` is the right call here, and safe
 *
 * `milsymbol` returns SVG as a **string**. Parsing it into React elements would
 * mean shipping a second SVG parser to avoid a risk that does not exist: the
 * only input to `asSVG()` is a 20-character code assembled from a frozen table
 * by `sidcFor`, and an unrecognised `typeId` selects a table key rather than
 * reaching the output. No value from the wire is ever interpolated into the
 * markup.
 *
 * ## Caching
 *
 * Symbol construction is the expensive part and both vendored renderers cache
 * it for the same reason. Keyed by every input that changes the output.
 */

interface MilSymbolLike {
  asSVG(): string;
}

interface MilSymbolCtor {
  new (sidc: string, options: Record<string, unknown>): MilSymbolLike;
}

const cache = new Map<string, string>();

function svgFor(sidc: string, size: number, colour: string): string {
  const key = sidc + '|' + String(size) + '|' + colour;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const Symbol_ = (ms as unknown as { Symbol: MilSymbolCtor }).Symbol;
  // milsymbol hard-codes `font-family="Arial"` on its icon letters and its own
  // `fontfamily` option does not reach them (verified against 3.0.4). The
  // vendored renderers embed the same library and inherit the same attribute, so
  // it is corrected once in `theme/tokens.css` for every source at once rather
  // than rewritten here for one of them.
  const svg = new Symbol_(sidc, {
    size,
    // Single-colour: the console's palette carries side and health already, and
    // 2525's own fill colours fight every theme token in the sheet.
    monoColor: colour,
    outlineWidth: 0,
    infoFields: false,
  }).asSVG();
  cache.set(key, svg);
  return svg;
}

export function MilSymbol(props: {
  readonly typeId: string;
  readonly segment: SidcSegment;
  readonly affiliation: SidcAffiliation;
  readonly rung?: Rung | null;
  readonly size?: number;
  readonly colour?: string;
  readonly box?: number;
}): JSX.Element {
  const sidc = sidcFor({
    typeId: props.typeId,
    segment: props.segment,
    affiliation: props.affiliation,
    rung: props.rung ?? null,
  });
  const size = props.size ?? 22;
  const box = props.box ?? 34;
  const colour = props.colour ?? 'currentColor';

  return (
    <span
      data-testid="mil-symbol"
      data-sidc={sidc}
      aria-hidden="true"
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: box,
        height: box,
        overflow: 'hidden',
      }}
      dangerouslySetInnerHTML={{ __html: svgFor(sidc, size, colour) }}
    />
  );
}
